/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { StatusBadge } from '@euler/app/components/StatusBadge';
import { TagView } from '@euler/app/components/TagView';
import { Timestamp } from '@euler/app/components/Timestamp';
import { Typography } from '@euler/app/components/typography';
import { Avatar } from '@euler/components/Avatar';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TaskStatusInfo, taskStatusFromOrderStatus } from '@euler/model';
import { ReceptionOrderStatus } from '@euler/model/enum';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { parseTagsFromString } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import clr from 'color';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import isBefore from 'date-fns/isBefore';
import { memo, useCallback, useMemo } from 'react';
import {
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';

const kLogoSize = 20;

type Item = SearchOrderResultItem;

type Props = {
  item: Item;
  style?: StyleProp<ViewStyle>;
  showFinishDate?: boolean;
  hideStatusLabel?: boolean;
  showRollbackDetail?: boolean;
  showCancelReason?: boolean;
  width?: number;
  onPress?: (item: Item) => void;
};

export const OrderSearchResultListItem = memo(
  ({
    item,
    style,
    hideStatusLabel,
    showCancelReason,
    showRollbackDetail,
    onPress,
  }: Props) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();

    const tags =
      item.tags != null && Array.isArray(item.tags)
        ? (item.tags as string[])
        : parseTagsFromString(item.tags ?? '常规保养');

    const statusInfo = useMemo(() => statusInfoFromItem(item), [item]);

    const onTaskPress = useCallback(() => {
      onPress?.(item);
    }, [onPress, item]);

    const hasTimedOut =
      (item.status === ReceptionOrderStatus.Pending ||
        item.status === ReceptionOrderStatus.InProgress) &&
      item.estimatedFinishTime != null &&
      isBefore(dateFromValue(item.estimatedFinishTime), new Date());

    return (
      <TouchableOpacity
        css={`
          background-color: ${hasTimedOut ? '#f5eee6' : '#fff'};
          border-radius: ${rpx(10)}px;
          box-shadow: 0 0 ${rpx(5)}px
            ${hasTimedOut ? 'rgba(255, 138, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
          elevation: 5;
          border-color: ${hasTimedOut ? StatusColors.Warn : '#fff'};
          border-width: ${hasTimedOut ? rpx(1.5) : 0}px;
        `}
        style={style}
        onPress={onTaskPress}
      >
        <HStack
          css={`
            color: white;
            padding-top: ${styles.header.paddingTop}px;
            padding-bottom: ${styles.header.paddingBottom}px;
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(15)}px;
            flex-direction: row-reverse;
          `}
        >
          <Img
            uri={item.vehicleImageUrl}
            source={
              item.vehicleImageUrl
                ? undefined
                : require('@euler/assets/img/default-car-img.png')
            }
            css={`
              width: ${styles.vehicleImage.width}px;
              height: ${styles.vehicleImage.height}px;
              flex-shrink: 0;
              flex-grow: 0;
            `}
          />
          <View
            css={`
              flex: 1;
              margin-right: ${rpx(15)}px;
              min-height: ${styles.headerContent.height}px;
              justify-content: center;
            `}
          >
            {/** license plate no - model name */}
            <HStack justifyContent="flex-start" alignItems="center">
              {item.vehicleBrandLogo ? (
                <Img
                  uri={item.vehicleBrandLogo}
                  css={`
                    width: ${rpx(kLogoSize)}px;
                    height: ${rpx(kLogoSize)}px;
                    flex-shrink: 0;
                    flex-grow: 0;
                  `}
                />
              ) : (
                <Avatar
                  name={item.vehicleName ?? item.licensePlateNo}
                  css={`
                    width: ${rpx(kLogoSize)}px;
                    height: ${rpx(kLogoSize)}px;
                    flex-shrink: 0;
                    flex-grow: 0;
                    border-radius: ${kLogoSize}px;
                  `}
                  textStyle={{
                    fontSize: rpx(12),
                  }}
                />
              )}
              <Typography.Label
                family="medium"
                size={styles.licensePlateNo.fontSize}
                numberOfLines={1}
                lineHeight={styles.licensePlateNo.lineHeight}
                color={
                  item.isRollback
                    ? StatusColors.Danger
                    : item.status === ReceptionOrderStatus.Cancelled
                    ? StatusColors.Warn
                    : '#333'
                }
                css={`
                  margin-left: ${rpx(5)}px;
                `}
                decorationLine={
                  item.status === ReceptionOrderStatus.Cancelled
                    ? 'line-through'
                    : undefined
                }
                decorationStyle={
                  item.status === ReceptionOrderStatus.Cancelled
                    ? 'solid'
                    : undefined
                }
              >
                {item.licensePlateNo}
                {item.hasChildOrders ? (
                  <Text
                    style={{
                      fontFamily: 'system',
                      fontWeight: '400',
                      fontSize: 14,
                      color: StatusColors.Info,
                    }}
                  >
                    {' *'}
                  </Text>
                ) : (
                  ''
                )}
              </Typography.Label>
              {item.vehicleModel !== '未知' && (
                <VehicleModelView danger={item.isRollback ?? false}>
                  <Typography.Label
                    color={item.isRollback ? StatusColors.Danger : '#0077ff'}
                    numberOfLines={1}
                    size={styles.vehicleName.fontSize}
                    lineHeight={styles.vehicleName.lineHeight}
                  >
                    {item.vehicleModel}
                  </Typography.Label>
                </VehicleModelView>
              )}
              {hasTimedOut && (
                <Typography.Text
                  color={StatusColors.Warn}
                  black
                  ml={rpx(10)}
                  numberOfLines={1}
                >
                  [已超时]
                </Typography.Text>
              )}
            </HStack>

            {/** order basic information */}
            <HStack
              justifyContent="flex-start"
              css={`
                padding-left: ${rpx(2)}px;
                margin-top: ${rpx(4)}px;
              `}
            >
              <Timestamp
                timestamp={item.createdAt}
                iconColor={styles.infoLabel.color}
                value={
                  item.finishedAt != null
                    ? differenceInSeconds(
                        dateFromValue(item.finishedAt),
                        dateFromValue(item.createdAt),
                        { roundingMethod: 'round' },
                      )
                    : undefined
                }
                label={
                  item.status === ReceptionOrderStatus.Finished ? '耗时' : ''
                }
                labelStyle={{
                  fontSize: styles.infoLabel.fontSize,
                  lineHeight: styles.infoLabel.lineHeight,
                  fontFamily: FontFamily.NotoSans.Light,
                  color: styles.infoLabel.color,
                }}
              />
              {item.vehicleMileage > 0 && (
                <>
                  <InlineSeparator />
                  <InfoLabel>{item.vehicleMileage}KM</InfoLabel>
                </>
              )}
              <InlineSeparator />
              <InfoLabel>业务员: {item.serviceAgentName}</InfoLabel>
            </HStack>

            {/** creation time and finish time */}
            <HStack
              justifyContent="flex-start"
              css={`
                padding-left: ${rpx(2)}px;
                margin-top: ${rpx(4)}px;
              `}
            >
              <InfoLabel
                css={`
                  letter-spacing: -0.3px;
                `}
              >
                {`建单时间: ${formatDateTime(item.createdAt, 'M/d HH:mm')}`}
              </InfoLabel>
              {item.finishedAt ? (
                <InfoLabel
                  css={`
                    margin-left: ${rpx(8)}px;
                    letter-spacing: -0.3px;
                  `}
                >
                  {`完工时间: ${formatDateTime(item.finishedAt, 'M/d HH:mm')}`}
                </InfoLabel>
              ) : null}
              {item.canceledAt ? (
                <InfoLabel
                  css={`
                    margin-left: ${rpx(8)}px;
                    letter-spacing: -0.3px;
                  `}
                >
                  {`闭单时间: ${formatDateTime(item.canceledAt, 'M/d HH:mm')}`}
                </InfoLabel>
              ) : (item.status === ReceptionOrderStatus.Pending ||
                  item.status === ReceptionOrderStatus.InProgress) &&
                item.estimatedFinishTime != null ? (
                <InfoLabel
                  css={`
                    margin-left: ${rpx(8)}px;
                    letter-spacing: -0.3px;
                  `}
                >
                  {`预计完工: ${formatDateTime(
                    item.estimatedFinishTime,
                    'M/d HH:mm',
                  )}`}
                </InfoLabel>
              ) : null}
            </HStack>
            {item.isRollback && showRollbackDetail ? (
              <HStack
                justifyContent="flex-start"
                alignItems="center"
                css={`
                  margin-top: ${rpx(8)}px;
                `}
              >
                <Typography.Label
                  css={`
                    color: ${StatusColors.Danger};
                    font-size: ${rpx(13)}px;
                    line-height: ${rpx(15)}px;
                    font-family: ${FontFamily.NotoSans.Regular};
                  `}
                >
                  返单原因: {item.rollbackReason ?? '无'}
                </Typography.Label>
              </HStack>
            ) : null}

            {item.status === ReceptionOrderStatus.Cancelled &&
            showCancelReason ? (
              <HStack
                justifyContent="flex-start"
                alignItems="center"
                css={`
                  margin-top: ${rpx(8)}px;
                `}
              >
                <Typography.Label
                  css={`
                    color: ${StatusColors.Warn};
                    font-size: ${rpx(13)}px;
                    line-height: ${rpx(15)}px;
                    font-family: ${FontFamily.NotoSans.Regular};
                  `}
                >
                  闭单原因: {item.cancelReason ?? '无'}
                </Typography.Label>
              </HStack>
            ) : null}
          </View>
        </HStack>

        {/** separator */}
        <View
          css={`
            background-color: ${hasTimedOut ? StatusColors.Warn : '#ddd'};
            height: ${StyleSheet.hairlineWidth}px;
            margin: 0 ${rpx(10)}px;
          `}
        />

        {/** status label, tags and observe label */}
        <HStack
          justifyContent="flex-start"
          css={`
            padding-top: ${styles.footer.paddingTop}px;
            padding-bottom: ${styles.footer.paddingBottom}px;
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(15)}px;
          `}
        >
          <HStack
            justifyContent="flex-start"
            css={`
              flex: 1;
            `}
          >
            {!hideStatusLabel && item.status != null && (
              <StatusBadge
                bordered
                shade
                isParentTask={item.isMainOrder && item.hasChildOrders}
                statusInfo={statusInfo}
                isRollback={item.isRollback ?? false}
                textStyle={{
                  fontSize: styles.statusBadge.fontSize,
                  lineHeight: styles.statusBadge.lineHeight,
                }}
                css={`
                  flex-shrink: 0;
                  flex-grow: 0;
                  border-radius: ${rpx(3)}px;
                  height: ${styles.statusBadge.height}px;
                  padding: 0 ${styles.statusBadge.height / 3}px;
                  margin-right: ${rpx(5)}px;
                `}
              />
            )}
            {tags.slice(0, 3).map(tag => (
              <TagView
                key={tag}
                value={tag}
                css={`
                  height: ${styles.statusBadge.height}px;
                  margin-right: 5px;
                `}
              />
            ))}
          </HStack>
          <View
            css={`
              border-radius: ${rpx(3)}px;
              padding-left: ${rpx(4)}px;
              padding-right: ${rpx(4)}px;
              background-color: ${item.subscriptions?.length
                ? 'rgba(43,188,43,0.15)'
                : clr(StatusColors.Danger).alpha(0.25).string()};
              justify-content: space-between;
              flex-direction: row;
              margin-left: ${rpx(5)}px;
              align-items: center;
              flex-wrap: nowrap;
              height: ${styles.statusBadge.height}px;
            `}
          >
            <Icon
              name="wechat"
              size={16}
              color={
                item.subscriptions?.length ? '#2bb72b' : StatusColors.Danger
              }
              css={`
                margin-right: ${rpx(3)}px;
              `}
            />
            <Typography.Label
              numberOfLines={1}
              light
              size={styles.statusBadge.fontSize}
              lineHeight={styles.statusBadge.lineHeight}
              color={
                item.subscriptions?.length ? '#2bb72b' : StatusColors.Danger
              }
            >
              {item.subscriptions?.length ? '已关注' : '未关注'}
            </Typography.Label>
          </View>
        </HStack>
      </TouchableOpacity>
    );
  },
);

const InlineSeparator = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        height: ${rpx(12)}px;
        width: ${rpx(1.5)}px;
        margin-left: ${rpx(5)}px;
        margin-right: ${rpx(5)}px;
        background-color: #dedede;
      `}
    />
  );
});

const VehicleModelView = memo(
  ({ danger, ...props }: ViewProps & { danger?: boolean }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          border-width: 1px;
          border-color: ${danger ? StatusColors.Danger : '#0077ff'};
          border-radius: ${rpx(5)}px;
          padding-left: ${rpx(6)}px;
          padding-right: ${rpx(6)}px;
          margin-left: ${rpx(5)}px;
          align-items: center;
          justify-content: center;
        `}
        {...props}
      />
    );
  },
);

const InfoLabel = memo((props: Typography.TextBaseProps) => {
  const styles = useStyles();
  return (
    <Typography.Label
      light
      color={styles.infoLabel.color}
      size={styles.infoLabel.fontSize}
      lineHeight={styles.infoLabel.lineHeight}
      numberOfLines={1}
      {...props}
    />
  );
});

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () => ({
      header: {
        paddingTop: rpx(12),
        paddingBottom: rpx(10),
      },
      headerContent: {
        height: rpx(72),
      },
      vehicleImage: {
        width: rpx(80),
        height: rpx(60),
      },
      vehicleName: {
        fontSize: rpx(12),
        lineHeight: rpx(20),
      },
      licensePlateNo: {
        fontSize: rpx(18),
        lineHeight: rpx(24),
      },
      infoLabel: {
        fontSize: rpx(13),
        lineHeight: rpx(18),
        color: '#666',
      },
      footer: {
        paddingTop: rpx(10),
        paddingBottom: rpx(12),
      },
      statusBadge: {
        fontSize: rpx(11),
        lineHeight: rpx(18),
        height: rpx(24),
      },
      observeStatus: {
        fontSize: rpx(11),
        lineHeight: rpx(20),
      },
    }),
    [rpx],
  );
};

export const useOrderSearchResultItemMetrics = () => {
  const styles = useStyles();
  const headerHeight =
    styles.headerContent.height +
    styles.header.paddingTop +
    styles.header.paddingBottom;
  const footerHeight =
    styles.statusBadge.height +
    styles.footer.paddingTop +
    styles.footer.paddingBottom;
  const height = headerHeight + footerHeight + 1;
  return { headerHeight, footerHeight, height };
};

const statusInfoFromItem = (item: Item): TaskStatusInfo => {
  return {
    status: taskStatusFromOrderStatus(item.status),
    preInspectionStatus: item.preInspectionStatus,
    inspectionStatus: item.inspectionStatus,
    constructionStatus: item.constructionStatus,
    isConstructionJobScheduled: item.isConstructionJobScheduled,
    deliveryCheckStatus: item.deliveryCheckStatus,
    isObdInspected: item.isObdInspected,
    finishedAt: item.finishedAt ?? undefined,
  };
};
